import { useEffect } from 'react'
import tw from 'twin.macro'
import type { Stop } from '@getcircuit/schema'
import { mapAddressSearchResultItemToStop } from '@getcircuit/engine-client'
import { derivedDebounceFn } from '@getcircuit/web-utils'

import Autocomplete from '../Autocomplete/Autocomplete'
import type { AutocompleteChangeCallback } from '../Autocomplete/types'
import type { AddressInputProps, AddressInputDropdownValue } from './types'
import { Pin as PinIcon } from '../Icons'
import { DEBOUNCE_DELAY, EMPTY_DROPDOWN_ITEM } from '../../internal/consts'
import { EngineClient } from '../../internal/circuitEngine'
import type { DropdownItem } from '../DropdownAtoms/types'
import { useSafeState } from '../../hooks/useSafeState'

const Pin = tw(PinIcon)`w-4 h-4 mt-1 text-default-muted`

const debouncedAddressSearch = derivedDebounceFn(
  EngineClient.searchAddress,
  ({ query }) => {
    return query && query.length > 2
      ? DEBOUNCE_DELAY.short
      : DEBOUNCE_DELAY.long
  },
)

function mapStopToDropdownItem(stop: Stop) {
  return {
    title: stop.addressLineOne,
    subtitle: stop.addressLineTwo,
    startSlot: <Pin outline />,
    value: stop,
  }
}

function AddressInput({
  initialAddress = '',
  onAddressChange,
  placeholder,
  placeId,
  preventEmpty,
  searchMode = 'suggested_only',
  searchToken,
  stops = [],
  currentLocation,
  ...forwardedProps
}: AddressInputProps) {
  const [selectedAddress, setSelectedAddress] = useSafeState<
    DropdownItem<Stop>
  >({
    title: initialAddress,
    value: null,
  })

  useEffect(
    function updateLocalAddressOnPropChange() {
      setSelectedAddress((address) => ({ ...address, title: initialAddress }))
    },
    [initialAddress, setSelectedAddress],
  )

  const itemFinder = async (searchTerm: string) => {
    if (searchTerm == null) return

    const query = searchTerm.trim()

    if (query.length === 0) return

    const { debounced, data } = await debouncedAddressSearch({
      query,
      stops,
      searchToken,
      placeId,
      searchMode,
      current: currentLocation,
    })

    if (debounced || data == null) return

    // ignore if query has returned an error
    // todo: should we make errors more evident here?
    if (data.errors) {
      data.errors.forEach((apiError) => {
        console.error(apiError.message, apiError)
      })

      return
    }

    const autoCompleteOptions = data.suggestions
      .map(mapAddressSearchResultItemToStop)
      .map(mapStopToDropdownItem)

    return autoCompleteOptions
  }

  const handleAddressChange: AutocompleteChangeCallback<AddressInputDropdownValue> =
    async (changes) => {
      if (changes.selectedItem?.value == null) {
        setSelectedAddress(EMPTY_DROPDOWN_ITEM)

        onAddressChange?.({
          ...changes,
          selectedItem: EMPTY_DROPDOWN_ITEM,
        })

        return
      }

      const geocodeResult = await EngineClient.geocodeStop({
        stops,
        searchToken: changes.selectedItem.value.searchToken,
      })

      if (geocodeResult == null) {
        return
      }

      const geocodedItem = {
        title: `${geocodeResult.addressLineOne}, ${geocodeResult.addressLineTwo}`,
        value: geocodeResult,
      }

      setSelectedAddress(geocodedItem)

      onAddressChange?.({ ...changes, selectedItem: geocodedItem })
    }

  return (
    <Autocomplete
      autoFocusFirstItem
      itemFinder={itemFinder}
      onSelectedItemChange={handleAddressChange}
      placeholder={placeholder}
      preventEmpty={preventEmpty}
      selectedItem={selectedAddress}
      {...forwardedProps}
    />
  )
}

export default AddressInput
